import { Image } from "@chakra-ui/react";
import { NFTEvent, NFTEventInfo } from "@models/event";
import { isEmpty } from "lodash";
import Link from "next/link";
import { useRouter } from "next/router";
import { ReactNode, useMemo } from "react";
import { BiRightArrowAlt } from "react-icons/bi";
import EventDetailCountdown from "./EventDetailCountdown";

const EventDetailBanner = ({
  event,
  info,
}: {
  event: NFTEvent;
  info: NFTEventInfo;
}) => {
  const router = useRouter();
  const queryParams = useMemo(() => {
    if (!router.query || isEmpty(router.query)) return "";
    try {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(router.query)) {
        if (!value) continue;
        if (Array.isArray(value)) {
          params.append(key, value.join(","));
        } else {
          params.append(key, value);
        }
      }
      return `?${params.toString()}`;
    } catch (error) {
      return "";
    }
  }, [router.query]);

  const ActionButton = ({
    fill: fill = true,
    disabled,
    children,
  }: {
    fill?: boolean;
    disabled?: boolean;
    children?: ReactNode;
  }) => {
    return (
      <div
        className={
          "p-1 flex flex-row justify-center items-center flex-grow w-full relative text-center font-semibold first:rounded-bl-xl last:rounded-br-xl cursor-pointer shadow-lg " +
          (disabled
            ? "bg-neutral-600"
            : fill
            ? "last:bg-primary-300 hover:bg-primary-300"
            : "")
        }
        style={{
          backgroundImage:
            "linear-gradient(173deg, rgb(0, 240, 255) 0%, rgb(38, 87, 212) 97%)",
        }}
      >
        {children}
      </div>
    );
  };

  const CustomActionButton = ({ url, name }: { url: string; name: string }) => {
    return (
      <ActionButton>
        <div>
          <a
            className="p-4 w-full h-full"
            href={url}
            target="_blank"
            rel="noreferrer"
          >
            {name}
          </a>
        </div>
      </ActionButton>
    );
  };

  return (
    <div className="max-w-full w-full mb-8 z-10">
      <div className="flex flex-col shadow-lg rounded-none rounded-t-xl overflow-hidden">
        <Image
          maxH="28rem"
          objectFit="fill"
          src={
            info.hero ||
            info.banner ||
            (info.gallery && info.gallery.length > 0
              ? info.gallery[0]
              : "/images/event-placeholder.svg")
          }
          alt={info.name}
        />
        <div className="flex flex-row w-full">
          <ActionButton>
            <Link href={`/checkout${queryParams}`}>
              <a className="p-4 w-full h-full flex flex-row items-center justify-center text-white ">
                Buy <BiRightArrowAlt className="text-xl ml-1" />
              </a>
            </Link>
          </ActionButton>
          {!isEmpty(info.customActions) &&
            info.customActions!.map((action, i) => (
              <CustomActionButton key={i} url={action.url} name={action.name} />
            ))}
        </div>
      </div>

      {!!event.customizations?.showCountdown && (
        <EventDetailCountdown event={event} />
      )}
    </div>
  );
};

export default EventDetailBanner;
